import { useRef, useEffect, useState } from 'react';
import './Projects.css';

// Import screenshots - you'll add these to /public/screenshots/

  const PROJECTS = [
  {
    id: 'tidyup',
    num: '01',
    title: 'TidyUp',
    tagline: 'Shared chores made simple.',
    description: 'My roommates and I kept forgetting who was supposed to clean what. So I built TidyUp — a chore rotation app that automatically reassigns tasks every week, lets everyone check off completions, and uses a join code so any household can set it up in minutes.',
    tags: ['JavaScript', 'HTML/CSS', 'Python', 'FastAPI', 'Tailwind', 'LocalStorage'],
    screenshot: '/screenshots/tidyup.png',
    links: { github: 'https://github.com/Kelechi-Onwuka/TidyUp', live: 'https://kelechi-onwuka.github.io/TidyUp/' },
    accent: '#00FFA3',
  },
  {
    id: 'budgetnest',
    num: '02',
    title: 'BudgetNest',
    tagline: 'Finance made approachable.',
    description: 'Led a team of four as front-end design lead on this smart monthly budget planner. Designed the layout and UX, guided the team, and built income and expense dashboards with Flask backend routes. Focused on making finance feel human, not overwhelming.',
    tags: ['Python', 'Flask', 'HTML/CSS', 'JavaScript'],
    screenshot: '/screenshots/budgetnest.png',
    links: { github: 'https://github.com/Kelechi-Onwuka/BudgetNest' },
    accent: '#A78BFA',
  },
  {
    id: 'thinkflip',
    num: '03',
    title: 'ThinkFlip',
    tagline: 'Study smarter, not harder.',
    description: 'Finals were coming and I had no time for physical flashcards. So I built my own — unlimited decks, flip and edit cards, study for free. No paywall, no account needed. My friends and I still use it every midterm and finals season.',
    tags: ['JavaScript', 'HTML/CSS', 'LocalStorage'],
    screenshot: '/screenshots/thinkflip.png',
    links: { github: 'https://github.com/Kelechi-Onwuka/ThinkFlip', live: 'https://kelechi-onwuka.github.io/ThinkFlip/' },
    accent: '#7DD3FC',
  },
  {
    id: 'valentine',
    num: '04',
    title: "Valentine's Day",
    tagline: 'Interactive web experience.',
    description: 'A playful interactive website with moving buttons and an animated love letter. Built to showcase creative JavaScript sequencing, responsive themed layouts, and engaging micro-interactions.',
    tags: ['HTML', 'CSS', 'JavaScript'],
    screenshot: '/screenshots/valentines.png',
    links: { github: 'https://github.com/Kelechi-Onwuka/Personal-Valentine-s-Day-Website', live: 'https://kelechi-onwuka.github.io/Personal-Valentine-s-Day-Website/' },
    accent: '#F472B6',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function ProjectCard({ project, index }) {
  const [ref, inView] = useInView();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      className={`project-card ${inView ? 'animate-up' : ''}`}
      style={{ animationDelay: `${index * 0.1}s`, '--accent': project.accent }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="project-inner">
        <div className="project-meta">
          <span className="project-num">{project.num}</span>
          <div className="project-links">
            {project.links.github && (
              <a href={project.links.github} target="_blank" rel="noreferrer" className="proj-link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                GitHub
              </a>
            )}
            {project.links.live && (
              <a href={project.links.live} target="_blank" rel="noreferrer" className="proj-link proj-link-live">
                Live ↗
              </a>
            )}
          </div>
        </div>

        <div className="project-content">
          <div className="project-text">
            <h3 className="project-title">{project.title}</h3>
            <div className="project-tagline">{project.tagline}</div>
            <p className="project-desc">{project.description}</p>
            <div className="project-tags">
              {project.tags.map(t => (
                <span key={t} className="project-tag">{t}</span>
              ))}
            </div>
          </div>

          <div className="project-screenshot-wrap">
            <div className="project-screenshot-frame">
              <img
                src={project.screenshot}
                alt={project.title + ' screenshot'}
                className="project-screenshot-img"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="project-screenshot-placeholder">
                <span>{project.title}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="project-accent-line" />
    </div>
  );
}

export default function Projects() {
  return (
    <section className="section" id="projects">
      <div className="container">
        <div className="section-label">selected work</div>
        <h2 className="section-title">Things I've built.</h2>
        <div className="projects-list">
          {PROJECTS.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}
